import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  Divider,
  Stack,
  styled,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  Download,
  Gavel,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Launch,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { useState, useEffect, useContext, useRef, memo } from 'react';

import { ChatContext } from 'components/ChatBV/context';
import { buildCourtOpinionSystemMessageRequest } from 'components/ChatBV/utils/conversationUtils';
import { OpinionResult } from 'routes/Dashboard/CourtCases';
import {
  useCourtCaseOpinionsSummary,
  useCourtOpinionDetails,
} from 'routes/Dashboard/CourtCases/api';
import {
  OpinionContentHtml,
  StyledDownloadButton,
} from 'routes/Dashboard/CourtCases/component';
import { processHtmlWithCitations } from 'routes/Dashboard/CourtCases/utils';

import { CourtOpinionModal } from './CourtOpinionModal';
import { OpinionSelect } from './OpinionSelect';
import { SimilarCasesCard } from './SimilarCasesCard';
import { ToggleOpinionsButton } from './ToggleButton';

interface CourtOpinionsPageProps {
  item?: OpinionResult;
  caseName?: string; // Optional case name from metadata for display when restoring from history
  existingOpinionSummaryContextId?: string; // Context ID from existing message to prevent duplicate sends
}

export const CourtOpinionsPage = memo(
  ({
    item,
    caseName,
    existingOpinionSummaryContextId,
  }: CourtOpinionsPageProps) => {
    const StyledCourtOpinionsStack = styled(Stack)(({ theme }) => ({
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      marginBottom: theme.spacing(2),
    }));

    const CollapsedButton = styled(Button)(({ theme }) => ({
      border: 'none',
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&:focus': {
        backgroundColor: 'transparent',
      },
      '&:active': {
        backgroundColor: 'transparent',
      },
    }));
    const theme = useTheme();
    const { isMobile } = useScreenSize();
    const [activeView, setActiveView] = useState<'opinions' | 'similarCases'>(
      'opinions'
    );
    const cluster_id = item?.cluster_id?.toString();
    const result = useCourtOpinionDetails(cluster_id);
    const { opinionsQuery, similarCasesQuery } = result;

    const { data: opinionsData } = opinionsQuery;
    const {
      data: similarCasesData,
      isLoading: isSimilarCasesLoading,
      error: similarCasesError,
    } = similarCasesQuery;
    const [collapsed, setCollapsed] = useState(false);
    const [selectedOpinionIndex, setSelectedOpinionIndex] = useState(0);
    const [openOpinionModal, setOpenOpinionModal] = useState(false);
    const { trackEventGA4, logError } = useContext(AppConfig);
    const { handleConversation, lastContextId } = useContext(ChatContext);
    const systemMessageSentRef = useRef<string | null>(null);
    const handleConversationRef = useRef(handleConversation);
    const lastContextIdRef = useRef(lastContextId);

    // Keep refs updated
    useEffect(() => {
      handleConversationRef.current = handleConversation;
      lastContextIdRef.current = lastContextId;
    }, [handleConversation, lastContextId]);

    const opinionData = item;

    // Get available opinions from opinionsData (which includes headmatter)
    const availableOpinions = opinionsData || [];
    const selectedOpinion = availableOpinions[selectedOpinionIndex];

    // Determine if summary should be fetched (always true for valid opinions)
    const shouldFetchOpinionSummary = Boolean(
      selectedOpinion &&
        !('isHeadmatter' in selectedOpinion && selectedOpinion.isHeadmatter) &&
        (('download_url' in selectedOpinion && selectedOpinion.download_url) ||
          ('html_with_citations' in selectedOpinion &&
            selectedOpinion.html_with_citations) ||
          ('plain_text' in selectedOpinion && selectedOpinion.plain_text))
    );

    const {
      data: opinionSummaryData,
      isLoading: isLoadingOpinionSummary,
      isError: hasOpinionSummaryError,
    } = useCourtCaseOpinionsSummary(selectedOpinion, shouldFetchOpinionSummary);

    // Send system message to smartsearchbot when summary is available (only once per summary)
    useEffect(() => {
      // Only send if we have summary data, it's not loading, we have cluster_id
      if (!opinionSummaryData || isLoadingOpinionSummary || !item?.cluster_id) {
        return;
      }

      // Create a unique key for this summary (cluster_id + summary context_id if available)
      const summaryContextId = (opinionSummaryData as any)?.context_id;

      // If we have an existing context_id from the message, this means we're restoring from history
      // and the message was already sent - don't send again to avoid duplicate messages and teasers
      if (existingOpinionSummaryContextId) {
        // Mark as sent to prevent future sends
        systemMessageSentRef.current = `${item.cluster_id}-${
          summaryContextId || 'no-context'
        }`;
        return;
      }

      const messageKey = `${item.cluster_id}-${
        summaryContextId || 'no-context'
      }`;

      // Skip if we already sent a message for this exact summary
      if (systemMessageSentRef.current === messageKey) {
        return;
      }

      // Mark as sent immediately to prevent duplicate calls
      systemMessageSentRef.current = messageKey;

      // Send system message with cluster_id and summary context_id
      // Use refs to avoid re-executing when functions change
      const sendSystemMessage = async () => {
        try {
          const systemMessageRequest = buildCourtOpinionSystemMessageRequest(
            item.cluster_id,
            lastContextIdRef.current,
            summaryContextId,
            item.caseName || caseName
          );
          await handleConversationRef.current(systemMessageRequest);
        } catch (error) {
          logError(
            'Failed to send court opinion system message',
            error as Error
          );
          // Reset flag on error so it can retry if needed
          systemMessageSentRef.current = null;
        }
      };

      sendSystemMessage();
    }, [
      opinionSummaryData,
      isLoadingOpinionSummary,
      item?.cluster_id,
      item?.caseName,
      existingOpinionSummaryContextId,
      logError,
    ]);

    // Reset system message sent flag when cluster_id or selected opinion changes
    // BUT only if we're not restoring from history (no existingOpinionSummaryContextId)
    useEffect(() => {
      // Only reset if we're NOT restoring from history
      // If we're restoring from history, keep the ref to prevent duplicate sends
      if (!existingOpinionSummaryContextId) {
        systemMessageSentRef.current = null;
      }
    }, [
      item?.cluster_id,
      selectedOpinion?.id,
      existingOpinionSummaryContextId,
    ]);

    // Force refetch when switching to similar cases view
    useEffect(() => {
      if (
        activeView === 'similarCases' &&
        opinionsQuery.data &&
        !isSimilarCasesLoading
      ) {
        similarCasesQuery.refetch();
      }
    }, [
      activeView,
      opinionsQuery.data,
      isSimilarCasesLoading,
      similarCasesQuery,
    ]);

    const handleDownloadClick = () => {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'download_pdf',
        section_name: 'court_cases',
        trigger: 'pdf_button_clicked',
      });
    };

    const handleOpinionModalOpen = () => {
      setOpenOpinionModal(true);
    };

    // Reset selected opinion index when opinions data changes
    useEffect(() => {
      if (
        availableOpinions.length > 0 &&
        selectedOpinionIndex >= availableOpinions.length
      ) {
        setSelectedOpinionIndex(0);
      }
    }, [availableOpinions.length, selectedOpinionIndex]);

    return (
      <Box>
        {shouldFetchOpinionSummary && (
          <>
            {isLoadingOpinionSummary ? (
              <Box sx={{ mb: 3, mt: 2, textAlign: 'center', py: 3 }}>
                <Text variant="body2" color="text.secondary">
                  Generating summary...
                </Text>
              </Box>
            ) : (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <Text variant="h6" sx={{ mb: 0 }}>
                    Opinion Summary
                  </Text>
                </Box>

                {hasOpinionSummaryError || opinionSummaryData?.error ? (
                  <Text variant="body1" sx={{ color: 'error.main' }}>
                    Failed to generate summary. Please try again.
                  </Text>
                ) : (
                  <Text variant="body1" textAlign="justify" lineHeight={1.5}>
                    {opinionSummaryData?.summary || 'No summary available.'}
                  </Text>
                )}
              </Box>
            )}
          </>
        )}

        <StyledCourtOpinionsStack
          divider={<Divider />}
          sx={
            collapsed
              ? {
                  borderColor: `${theme.palette.primary.main}1F`,
                  background: `linear-gradient(90deg, ${theme.palette.primary.contrastText} 0%, ${theme.palette.primary.light} 100%)`,
                }
              : {}
          }
        >
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Gavel width={20} height={20} />
                  <Text variant="h5" sx={{ fontWeight: 600 }} m={0}>
                    Opinion
                  </Text>
                </Box>
                {collapsed ? (
                  <CollapsedButton
                    variant="outlined"
                    size="small"
                    onClick={() => setCollapsed(false)}
                  >
                    <KeyboardArrowDown sx={{ fontSize: 30 }} />
                  </CollapsedButton>
                ) : (
                  <CollapsedButton
                    variant="outlined"
                    size="small"
                    onClick={() => setCollapsed(true)}
                  >
                    <KeyboardArrowUp sx={{ fontSize: 30 }} />
                  </CollapsedButton>
                )}
              </Stack>
              <Box>
                <Text variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {opinionData?.caseName || caseName || ''}
                </Text>
                <Text variant="body2" color="text.secondary">
                  {opinionData?.court || ''}
                </Text>
              </Box>

              {!collapsed && (
                <Box sx={{ position: 'relative', overflow: 'visible' }}>
                  <Stack spacing={1}>
                    {opinionData?.citation && (
                      <Box>
                        <Text
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          Citation:
                        </Text>{' '}
                        <Text component="span" variant="body2">
                          {Array.isArray(opinionData?.citation)
                            ? opinionData?.citation.join(', ')
                            : opinionData?.citation}
                        </Text>
                      </Box>
                    )}

                    {opinionData?.caseNameFull &&
                      opinionData?.citation.length > 0 && (
                        <Box>
                          <Text
                            component="span"
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >
                            Case Full Name:
                          </Text>{' '}
                          <Text component="span" variant="body2">
                            {opinionData?.caseNameFull}
                          </Text>
                        </Box>
                      )}

                    {opinionData?.docketNumber && (
                      <Box>
                        <Text
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          Docket Number:
                        </Text>{' '}
                        <Text component="span" variant="body2">
                          {opinionData?.docketNumber}
                        </Text>
                      </Box>
                    )}
                    <Box>
                      <Text
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        Filed:
                      </Text>{' '}
                      <Text component="span" variant="body2">
                        {opinionData?.dateFiled
                          ? new Date(opinionData.dateFiled).toLocaleDateString()
                          : ''}
                      </Text>
                    </Box>

                    {opinionData?.status && (
                      <Box>
                        <Text
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          Status:
                        </Text>{' '}
                        <Text component="span" variant="body2">
                          {opinionData?.status}
                        </Text>
                      </Box>
                    )}

                    {opinionData?.suitNature && (
                      <Box>
                        <Text
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          Nature of Suit:
                        </Text>{' '}
                        <Text component="span" variant="body2">
                          {opinionData?.suitNature}
                        </Text>
                      </Box>
                    )}

                    {opinionData?.judge && (
                      <Box>
                        <Text
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          Judge:
                        </Text>{' '}
                        <Text component="span" variant="body2">
                          {opinionData?.judge}
                        </Text>
                      </Box>
                    )}
                  </Stack>

                  <ToggleOpinionsButton
                    activeView={activeView}
                    setActiveView={setActiveView}
                    isLoadingSimilarCases={isSimilarCasesLoading}
                    similarCasesData={
                      similarCasesError
                        ? { count: 0, results: [] }
                        : similarCasesData ?? { count: 0, results: [] }
                    }
                  />

                  {activeView === 'opinions' &&
                    availableOpinions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Text
                          variant="body1"
                          sx={{ fontWeight: 400, mb: 1 }}
                          color="text.secondary"
                        >
                          The text of these documents could have been obtained
                          by analyzing a scanned document and may have typos.
                        </Text>
                        <Stack
                          spacing={1}
                          sx={{ mb: 2 }}
                          flexDirection={isMobile ? 'column' : 'row'}
                          justifyContent="space-between"
                        >
                          <OpinionSelect
                            opinions={availableOpinions}
                            selectedIndex={selectedOpinionIndex}
                            onSelect={setSelectedOpinionIndex}
                          />
                          <Box display="flex" gap={1}>
                            {availableOpinions[selectedOpinionIndex] &&
                              'download_url' in
                                availableOpinions[selectedOpinionIndex] &&
                              availableOpinions[selectedOpinionIndex]
                                .download_url && (
                                <StyledDownloadButton
                                  href={
                                    availableOpinions[selectedOpinionIndex]
                                      .download_url
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={handleDownloadClick}
                                  sx={{
                                    mb: 0.5,
                                    borderColor: theme.palette.primary.main,
                                    width: isMobile ? '100%' : 'auto',
                                    justifyContent: isMobile
                                      ? 'center !important'
                                      : 'flex-start',
                                  }}
                                >
                                  <Download
                                    fontSize="small"
                                    sx={{ color: theme.palette.primary.main }}
                                  />
                                  <Text
                                    variant="body2"
                                    sx={{ color: theme.palette.primary.main }}
                                  >
                                    Download PDF
                                  </Text>
                                </StyledDownloadButton>
                              )}
                            <Button
                              onClick={handleOpinionModalOpen}
                              variant="outlined"
                              size="small"
                              sx={{
                                padding: '4px',
                                borderRadius: '100%',
                                minWidth: '35px',
                                minHeight: '35px',
                                border: `1px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Launch
                                fontSize="small"
                                sx={{ color: theme.palette.rats.main }}
                              />
                            </Button>
                          </Box>
                        </Stack>

                        <Stack spacing={2}>
                          <Box
                            sx={{
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                            }}
                          >
                            <OpinionContentHtml
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  processHtmlWithCitations(
                                    availableOpinions[selectedOpinionIndex]
                                      ?.html_with_citations || ''
                                  )
                                ),
                              }}
                            />
                          </Box>
                        </Stack>
                      </Box>
                    )}

                  {activeView === 'similarCases' && (
                    <Box sx={{ mt: 2 }}>
                      <Text
                        variant="body1"
                        sx={{ fontWeight: 400, mb: 2 }}
                        color="text.secondary"
                      >
                        The Similar Cases query helps you explore precedents or
                        comparable rulings. It analyzes textual similarities to
                        a given case to build a list of similar cases, their
                        names, links and filing dates.
                      </Text>
                      <SimilarCasesCard
                        similarCasesData={
                          similarCasesError
                            ? { count: 0, results: [] }
                            : similarCasesData ?? { count: 0, results: [] }
                        }
                        isLoading={isSimilarCasesLoading}
                      />
                    </Box>
                  )}

                  <Box marginTop={3.5}>
                    <Text
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'justify' }}
                    >
                      PLEASE BE ADVISED: Court case information contained in our
                      reports may not always be 100% up-to-date, accurate or
                      complete, since data is pulled from records maintained by
                      various state, county, municipal and other government
                      agencies and their contents may not be fully digitized,
                      updated or comprehensive. Please use our results as no
                      more than a point of departure and certainly no substitute
                      for your own due diligence.
                    </Text>
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
          {openOpinionModal && (
            <CourtOpinionModal
              open={openOpinionModal}
              onClose={() => setOpenOpinionModal(false)}
              activeView={activeView}
              availableOpinions={availableOpinions}
              selectedOpinionIndex={selectedOpinionIndex}
              setSelectedOpinionIndex={setSelectedOpinionIndex}
              handleDownloadClick={handleDownloadClick}
              cluster_id={cluster_id}
            />
          )}
        </StyledCourtOpinionsStack>
      </Box>
    );
  }
);
